import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import PreviewBadge from "@/components/sites/PreviewBadge";
import { EstateHeader, EstateFooter, EstateStickyCta } from "@/components/sites/EstateChrome";
import { useBrand, useTenantPath } from "@/lib/brand";
import { withPreview } from "@/lib/sitePreview";

/**
 * The shell every inner page of the Estate theme wears: head tags, chrome,
 * a page head with breadcrumbs, and the closing call to action.
 *
 * It exists because the eight content pages — sell, let, about, contact,
 * fees, guides, a guide, the legal documents — are the same page with
 * different words in it, and eight copies of a header, a footer, a sticky
 * mobile bar and a breadcrumb list is eight places for one of them to drift.
 * The listing, valuation, register and area pages keep their own shells:
 * each of those has a hero the others do not.
 */
export default function EstatePage({
  title, description, eyebrow, heading, lede, crumbs = [],
  children, cta = true, preview = true, wide = false, jsonLd,
}) {
  const brand = useBrand();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const p = (href) => withPreview(href, preview);

  return (
    <div className="fs">
      <SeoHead title={title} description={description} jsonLd={jsonLd} />
      <a className="skip" href="#main">Skip to content</a>
      {preview && <PreviewBadge />}
      <EstateHeader preview={preview} />

      <main id="main">
        <div className="page-head">
          <div className="container">
            {crumbs.length > 0 && (
              <ol className="crumbs">
                <li><Link to={p(tenantPath(""))}>Home</Link></li>
                {crumbs.map((c) => (
                  <li key={c.label}>
                    {c.to ? <Link to={p(c.to)}>{c.label}</Link> : c.label}
                  </li>
                ))}
              </ol>
            )}
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            <h1 className="display-l" style={{ marginTop: 10 }}>{heading}</h1>
            {lede && <p className="lede" style={{ marginTop: 18 }}>{lede}</p>}
          </div>
        </div>

        <div className={wide ? "" : "section"}>
          <div className={wide ? "" : "container"}>{children}</div>
        </div>

        {cta && (
          <section className="cta">
            <div className="container">
              <div>
                <div className="eyebrow">What is it worth?</div>
                <h2 className="display-m" style={{ marginTop: 12 }}>
                  Find out in sixty seconds.
                </h2>
              </div>
              <div className="cta-actions">
                <Link className="btn" to={p(tenantPath("valuation"))}>Get a valuation</Link>
                {biz.phone && (
                  <a className="btn ghost" href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`}>
                    {biz.phone}
                  </a>
                )}
              </div>
            </div>
          </section>
        )}
      </main>

      <EstateFooter preview={preview} />
      <EstateStickyCta preview={preview} />
    </div>
  );
}
